// ── DrawForm ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmt, fmtDate } from "../../../shared/format";
import { Btn, FInput, FInputNum, FSelect, Field, Modal, ReadOnlyForm } from "../../../shared/ui";
import { useFreshForm } from "../../../shared/hooks";
import { NotesPanel } from "../components/NotesPanel";
import { useApp } from "../../../shared/appContext";
import { t } from "../../../shared/terms";

export const DrawForm = ({ initial, loanid, readOnly }) => {
    const { api, role, modal, setModal, closeModal, saving, error, saveRec } = useApp();
    const todayISO = (() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
    const [f, set] = useFreshForm(initial||{
      expectedfunddate: todayISO, fundingamount:"", fundeddate:"", expecteddrawamount:"",
      drawfundedby:"", drawrequestedby:"", samedayinterestexemption: 0, capitalized: 0,
      collateralapprovalnote:"", loanid
    }, "Draws");

    const [fundedByOptions, setFundedByOptions] = useState([]);       // isdrawfunding
    const [requestedByOptions, setRequestedByOptions] = useState([]); // isportfoliomanager
    const [loan, setLoan] = useState(null);
    const [fundingWarn, setFundingWarn] = useState(false);           // funding-mismatch popup

    useEffect(()=>{
      const opt = users => [{value:"",label:"— Select —"}, ...users.map(u=>({value:u.id,label:u.FullName}))];
      Promise.all([
        api.get("getUsers?filter=isdrawfunding"),
        api.get("getUsers?filter=isportfoliomanager"),
      ]).then(([funders,pms])=>{ setFundedByOptions(opt(funders)); setRequestedByOptions(opt(pms)); })
      .catch(()=>{});
    },[]);

    const wasFunded = !!initial?.fundeddate;
    const showCollateral = !wasFunded;
    const canFund = !!role.isdrawfunding;          // only draw-funding users may edit funded-by / date / amount
    const canRequest = !!role.isportfoliomanager;  // only portfolio managers may edit the requestor fields

    useEffect(()=>{
      const lid = loanid || initial?.loanid;
      if (showCollateral && lid) api.get("getLoan?id="+lid).then(setLoan).catch(()=>setLoan(null));
    },[loanid, initial, showCollateral]);

    const ensureSelected = (opts, value, name) =>
      value && !opts.some(o => String(o.value) === String(value))
        ? [...opts, { value, label: (name || "Unknown user") + " (inactive)" }]
        : opts;
    const requestedOpts = ensureSelected(requestedByOptions, f.drawrequestedby, initial?.drawrequestedbyName);
    const fundedOpts    = ensureSelected(fundedByOptions,    f.drawfundedby,    initial?.drawfundedbyName);

    // ── Collateral / Excess Availability math ──
    const drawAmt     = parseFloat(f.expecteddrawamount) || 0;
    const currBal     = loan ? (parseFloat(loan.loanbalanceeod) || 0) : 0;
    const ea          = loan ? (parseFloat(loan.excessavailability) || 0) : 0;
    const proposedBal = currBal + drawAmt;
    const remainingEA = ea - drawAmt;
    const overAdvance = Math.max(0, -remainingEA);
    const sufficient  = remainingEA >= 0;
    const needsApproval = showCollateral && !!loan && !sufficient;
    const blockSave   = needsApproval && !(f.collateralapprovalnote||"").trim();

    // ── Funding-amount must equal expected (once a funding amount is entered) ──
    const fundAmt = parseFloat(f.fundingamount);
    const expAmt  = parseFloat(f.expecteddrawamount);
    const fundingEntered  = !isNaN(fundAmt);
    const fundingMismatch = fundingEntered && (isNaN(expAmt) || Math.abs(fundAmt - expAmt) > 0.005);

    // ── Required to save ─────────────────────────────────────────────────
    // A draw with no amount, no date, nobody who asked for it and nobody
    // assigned to fund it is a row that somebody has to chase by hand. The
    // server enforces the same four (see saveDraw) — this is here so the answer
    // arrives before the round trip, not instead of it.
    //
    // The red asterisks on the labels are the standing signal. This list only
    // disables Save and fills its tooltip; it deliberately does not render a
    // message of its own, which was a paragraph of prose repeating what four
    // asterisks already said.
    //
    // Zero is not an amount. A $0 draw parses fine and means nothing, so it is
    // refused along with a blank.
    const REQUIRED = [
      ["drawrequestedby",    t("drawRequestedBy")],
      ["expectedfunddate",   "Expected Fund Date"],
      ["expecteddrawamount", "Expected Draw Amount"],
      ["drawfundedby",       t("drawFundedBy")],
    ];
    const missing = REQUIRED.filter(([k]) => {
      const v = f[k];
      if (v === undefined || v === null || String(v).trim() === "") return true;
      if (k === "expecteddrawamount") { const n = parseFloat(v); return isNaN(n) || n <= 0; }
      return false;
    }).map(([, label]) => label);

    const attemptSave = () => {
      if (fundingMismatch) { setFundingWarn(true); return; }   // popup, block save
      saveRec("saveDraw", {...f, loanid:f.loanid||loanid}, initial?"Edited":"Added", "Draws",
        (initial?"Updated":"Added")+" draw", modal.onDone);
    };

    const ST = ({ title }) => <div style={{ gridColumn:"1/-1", fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:18, marginBottom:8, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight }}>{title}</div>;
    const RO = ({ label, value }) => <Field label={label}><div style={{ padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #ddd", background:"#f5f5f5", color:"#555", minHeight:18, whiteSpace:"pre-wrap" }}>{value||"—"}</div></Field>;
    const badge = (bg,fg)=>({ display:"inline-flex", alignItems:"center", gap:6, padding:"4px 10px", borderRadius:6, fontSize:12, fontWeight:700, background:bg, color:fg });
    const TA = { width:"100%", boxSizing:"border-box", padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #999", background:"#fff", color:"#1a1a1a", fontFamily:"inherit" };

    return <Modal title={readOnly?t("viewDraw"):initial?t("editDraw"):t("addDraw")} onClose={closeModal} wide
            onEdit={readOnly&&role.canEdit?()=>{ closeModal(); setModal({type:"draw-edit",rec:initial,loanid:initial.loanid,onDone:modal.onDone}); }:null}>
      <ReadOnlyForm readOnly={readOnly}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 24px" }}>

          <ST title="Requestor" />
          <Field label={t("drawRequestedBy")} required={!readOnly}><FSelect value={f.drawrequestedby} onChange={set("drawrequestedby")} options={requestedOpts} disabled={!canRequest} /></Field>
          <Field label="Expected Fund Date" required={!readOnly}><FInput value={f.expectedfunddate?.slice(0,10)||""} onChange={set("expectedfunddate")} type="date" disabled={!canRequest} /></Field>
          <Field label="Expected Draw Amount" required={!readOnly}><FInputNum value={f.expecteddrawamount} onChange={set("expecteddrawamount")} disabled={!canRequest} /></Field>
          {!readOnly && !canRequest && <div style={{ gridColumn:"1/-1", fontSize:11, color:"var(--color-text-secondary)", marginTop:2 }}>Requestor fields are editable only by portfolio managers.</div>}

          {showCollateral && (
            <>
              <ST title="Collateral" />
              <RO label="Most Recent Collateral Date" value={loan?.ea_collateralasofdate ? fmtDate(loan.ea_collateralasofdate) : "—"} />
              <RO label="Current Loan Balance (EOD)"  value={loan ? fmt(currBal) : "—"} />
              <RO label="Proposed New Loan Balance"    value={loan ? fmt(proposedBal) : "—"} />
              <RO label="Excess Availability"          value={loan ? fmt(ea) : "—"} />
              <div style={{ gridColumn:"1/-1", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", marginTop:2, marginBottom:4 }}>
                {!loan
                  ? <span style={{ fontSize:12, color:"var(--color-text-secondary)" }}>Loading collateral…</span>
                  : sufficient
                    ? <span style={badge("#dcfce7","#166534")}>✓ Sufficient Collateral</span>
                    : <span style={badge("#fde2e0","#993C1D")}>⚠ Insufficient Collateral</span>}
                {loan && (sufficient
                  ? <span style={{ fontSize:12, color:"var(--color-text-secondary)" }}>Availability remaining after draw: {fmt(remainingEA)}</span>
                  : <span style={{ fontSize:14, fontWeight:700, color:"#993C1D" }}>Over-advance by {fmt(overAdvance)}</span>)}
              </div>

              {needsApproval && !readOnly && (
                <div style={{ gridColumn:"1/-1", marginTop:2, marginBottom:6, padding:"10px 12px", background:"#fdecea", border:"1.5px solid #993C1D", borderRadius:6 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#993C1D", marginBottom:6 }}>
                    ⚠ Over-Advance Approval Justification — required to save this draw
                  </div>
                  <textarea value={f.collateralapprovalnote||""} onChange={e=>set("collateralapprovalnote")(e.target.value)} rows={2}
                    style={{ ...TA, border:"1.5px solid #993C1D" }}
                    placeholder="Explain the approval for advancing beyond excess availability…" />
                </div>
              )}

              {readOnly && f.collateralapprovalnote && (
                <div style={{ gridColumn:"1/-1", marginBottom:6, padding:"10px 12px", background:"#fdecea", border:"1.5px solid #993C1D", borderRadius:6 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#993C1D", marginBottom:4 }}>⚠ Over-Advance Approval Justification</div>
                  <div style={{ fontSize:13, color:"#1a1a1a", whiteSpace:"pre-wrap" }}>{f.collateralapprovalnote}</div>
                </div>
              )}
            </>
          )}

          {!showCollateral && f.collateralapprovalnote && (
            <>
              <ST title="Collateral" />
              <div style={{ gridColumn:"1/-1", padding:"10px 12px", background:"#fdecea", border:"1.5px solid #993C1D", borderRadius:6 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#993C1D", marginBottom:4 }}>⚠ Over-Advance Approval Justification</div>
                <div style={{ fontSize:13, color:"#1a1a1a", whiteSpace:"pre-wrap" }}>{f.collateralapprovalnote}</div>
              </div>
            </>
          )}

          <ST title="Approver" />
          {/* WHO IS ASSIGNED, sitting above the two fields that record what
              actually happened. It is required to create a draw, so the person
              entering one has to be able to set it — hence canRequest here while
              the two below stay canFund. The permission moved; the field did
              not. See the note under this section and fieldPerms.js. */}
          <Field label={t("drawFundedBy")} required={!readOnly}><FSelect value={f.drawfundedby} onChange={set("drawfundedby")} options={fundedOpts} disabled={!canRequest} /></Field>
          <Field label="Funded Date"><FInput value={f.fundeddate?.slice(0,10)||""} onChange={set("fundeddate")} type="date" disabled={!canFund} /></Field>
          <Field label="Funding Amount"><FInputNum value={f.fundingamount} onChange={set("fundingamount")} disabled={!canFund} /></Field>
          {!readOnly && !canFund && <div style={{ gridColumn:"1/-1", fontSize:11, color:"var(--color-text-secondary)", marginTop:2 }}>Funded Date and Funding Amount are editable only by draw-funding users.</div>}
          {!readOnly && fundingMismatch && (
            <div style={{ gridColumn:"1/-1", fontSize:12, fontWeight:600, color:"#993C1D", marginTop:2 }}>
              Funding Amount must equal Expected Draw Amount ({isNaN(expAmt) ? "—" : fmt(expAmt)}) to save. Leave it blank to save as pending.
            </div>
          )}

          <ST title="Not Common" />
          {/* Same-Day Interest Exemption */}
          <Field label="Same-Day Interest Exemption">
            <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:4 }}>
              <input
                type="checkbox"
                id="samedayinterestexemption"
                checked={!!f.samedayinterestexemption}
                onChange={e => set("samedayinterestexemption")(e.target.checked ? 1 : 0)}
                disabled={readOnly}
                style={{ width:16, height:16, cursor: readOnly ? "default" : "pointer" }}
              />
              <label htmlFor="samedayinterestexemption" style={{ fontSize:11, color:B.textMuted, cursor: readOnly ? "default" : "pointer" }}>
                When checked, no interest is charged to the borrower on the funded amount for the same day this draw is funded.
              </label>
            </div>
          </Field>
          {/* Capitalized */}
          <Field label="Capitalized">
            <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:4 }}>
              <input
                type="checkbox"
                id="capitalized"
                checked={!!f.capitalized}
                onChange={e => set("capitalized")(e.target.checked ? 1 : 0)}
                disabled={readOnly}
                style={{ width:16, height:16, cursor: readOnly ? "default" : "pointer" }}
              />
              <label htmlFor="capitalized" style={{ fontSize:11, color:B.textMuted, cursor: readOnly ? "default" : "pointer" }}>
                When checked, this draw is flagged as capitalized.
              </label>
            </div>
          </Field>
        </div>
      </ReadOnlyForm>
      {initial?.id && <NotesPanel scope="record" entitytype="Draw" entityid={initial.id} accountid={f.accountid} loanid={f.loanid} source="Draw" />}
      {!readOnly&&error&&<div style={{ color:"#993C1D",fontSize:12,marginBottom:8,marginTop:8 }}>{error}</div>}
      {!readOnly && blockSave && <div style={{ color:"#993C1D", fontSize:12, fontWeight:600, marginTop:8 }}>An Over-Advance Approval Justification is required before this draw can be saved.</div>}
      <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
        <Btn onClick={closeModal}>{readOnly?"Close":"Cancel"}</Btn>
        {!readOnly&&<Btn disabled={saving||blockSave||missing.length>0} onClick={attemptSave} variant="primary"
          title={missing.length ? "Still needed: " + missing.join(", ") : undefined}>{saving?"Saving...":"Save"}</Btn>}
      </div>

      {/* Funding mismatch popup */}
      {fundingWarn && (
        <Modal title="Funding Amount Doesn't Match" onClose={()=>setFundingWarn(false)}>
          <div style={{ fontSize:13, lineHeight:1.5, marginBottom:16 }}>
            The <strong>Funding Amount</strong> ({fmt(fundAmt)}) does not equal the
            <strong> Expected Draw Amount</strong> ({isNaN(expAmt) ? "—" : fmt(expAmt)}).
            A draw can only be funded for the exact expected amount — update the Funding Amount to match,
            or clear it to leave the draw pending approval.
          </div>
          <div style={{ display:"flex", justifyContent:"flex-end" }}>
            <Btn variant="primary" onClick={()=>setFundingWarn(false)}>OK</Btn>
          </div>
        </Modal>
      )}
    </Modal>;
  };
